import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ResultsDashboard.css';
import { API_ENDPOINTS, API_BASE_URL } from '../config';
import { transformSheetToResultsDashboardModel } from '../utils/transformResults';
import { IconLock, IconZap, IconRefresh, IconInfo, IconList, IconCheck, IconChevronRight, IconChevronDown, IconFilter } from '../components/Icons';
import { VectorResultsLoadingState } from '../components/VectorResultsLoadingState';
import SummaryCards from '../components/SummaryCards.js';
import ResultsControls from '../components/ResultsControls.js';
import StrategyRowCard from '../components/StrategyRowCard.js';
import { sortStrategies } from '../utils/strategySort';
import StrategyDetailsView, { StrategyFilterCompareControl, SpreadsheetView } from '../components/StrategyDetailsView.js';
import { computeStrategySheetMetrics } from '../utils/strategySheetMetrics';
import {
  DEFAULT_DISPLAY_FILTER,
  isDisplayFilterActive,
  dayPassesDisplayFilter,
  uniqueMonthKeysFromStrategyRows,
  RED_FOLDER_FILTER_ENABLED,
} from '../utils/strategyDisplayFilter';
import { loadUsHighImpactEvents } from '../utils/usHighImpactCalendar';
import {
  MULTI_STRAT_SHEET_NAME,
  isMultiStratName,
} from '../utils/vectorCapitalScenario';
import { getNasdaqRollWeekInfoForDay, isNasdaqFuturesContractType } from '../utils/cmeNasdaqFuturesRoll';
import StrategySummaryTable from '../components/StrategySummaryTable.js';
import NinjaTraderDashboard from '../components/NinjaTraderDashboard.js';
import BetaEndedCard from '../components/BetaEndedCard.js';
import VectorResultsSamplePreview from '../components/VectorResultsSamplePreview.js';
import CustomMultistratBuilderModal from '../components/CustomMultistratBuilderModal.js';
import { trackEvent } from '../utils/analytics';
import { getRiskloAuthToken, getStoredRiskloUserEmail } from '../utils/storedSession';
import { useDelayedTrueWhile } from '../hooks/useDelayedTrueWhile';
import { PnlScalingInfoModal, DEFAULT_PNL_SCALING_PARAGRAPHS } from '../components/PnlScalingInfoModal';
import { getContractTypeLabelsForStrategy, getContractTypeLabelsForCombo, comboHasMixedContractFamilies } from '../utils/strategyContractTypeOptions';
import { resolveStrategyInstrument, usesFuturesContractToggle, contractTypeLabelsForInstrument } from '../utils/strategyInstrument';
import { hasMyResultsAccess } from '../utils/tierAccess';
import { useBrandIdentity } from '../utils/brandIdentity';
import {
  userFacingStrategyFetchError, userFacingCurrentResultsError,
  isMembershipAccessDenied, membershipDeniedMessage, isMembershipDenialText,
} from '../utils/userFacingStrategyErrors';
import PageHeading from '../components/PageHeading';
import usePersistentTab from '../hooks/usePersistentTab';
import { filterSheetRowsByJourney, normalizeLiveSince } from '../utils/journeyView';
import ArrangeResultsTabs, { OPEN_ARRANGE_KEY } from '../components/results/ArrangeResultsTabs';
import { useAuth } from '../contexts/AuthContext';
import {
  getDataSourceNote, getResultsNavLabel, getEntityDisplayName, isVectorEntity,
} from '../utils/activeEntity';
import { getBearerAuthHeaders, getVectorApiHeaders } from '../utils/authFetchHeaders';

const VECTOR_RESULTS_LOADING_UI_DELAY_MS = 1000;
// Max saved custom strat combos per user. Must match MAX_COMBOS_PER_USER in
// server/routes/customMultistrats.js (server enforces; this is display only).
const MAX_CUSTOM_COMBOS = 5;

const ROLL_INDICATORS_STORAGE_KEY = 'risklo_show_roll_week_indicators';

function readRollIndicatorsFromStorage() {
  try {
    return localStorage.getItem(ROLL_INDICATORS_STORAGE_KEY) !== '0';
  } catch (_) {
    return true;
  }
}

/** Don’t show “Loading your account access…” unless tier checks run longer than this (avoids flash). */
const TIER_WAIT_UI_DELAY_MS = 750;
function formatWeekLabel(label) {
  if (!label) return '';
  // Try to parse known formats like MM.DD.YYYY without using Date (avoid TZ shifts)
  const dotParts = label.split('.');
  if (dotParts.length === 3) {
    const [mm, dd, yyyy] = dotParts;
    const mIdx = parseInt(mm, 10) - 1;
    const d = parseInt(dd, 10);
    const y = parseInt(yyyy, 10);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    if (
      !isNaN(mIdx) &&
      mIdx >= 0 &&
      mIdx < 12 &&
      !isNaN(d) &&
      d >= 1 &&
      d <= 31 &&
      !isNaN(y)
    ) {
      return `${months[mIdx]} ${d}, ${y}`;
    }
  }
  return label; // fallback
}

// Parse date from row label like "Fri (04.21.2025)" or "04.21.2025"
function parseDateFromRowLabel(label) {
  if (!label) return null;
  
  // Extract date pattern MM.DD.YYYY from various formats
  const dateMatch = label.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dateMatch) {
    const [, mm, dd, yyyy] = dateMatch;
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year > 1900) {
      // Return Date object for comparison
      return new Date(year, month - 1, day);
    }
  }
  return null;
}

// Get date range based on filter selection
function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  let startDate = null;
  let endDate = null;

  switch (filter) {
    case 'all-time':
      return { startDate: null, endDate: null }; // No filter
    
    case 'last-30-days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      endDate = now;
      break;
    
    case 'last-3-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      endDate = now;
      break;
    
    case 'last-6-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      endDate = now;
      break;
    
    case 'last-12-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 12);
      endDate = now;
      break;
    
    case '2024':
      startDate = new Date(2024, 0, 1);
      endDate = new Date(2024, 11, 31);
      break;
    
    case '2025':
      startDate = new Date(2025, 0, 1);
      endDate = new Date(2025, 11, 31);
      break;
    
    case 'custom':
      if (customStart) {
        startDate = new Date(customStart);
      }
      if (customEnd) {
        endDate = new Date(customEnd);
      }
      break;
    
    default:
      return { startDate: null, endDate: null };
  }

  return { startDate, endDate };
}

// Filter rows by date range
function filterRowsByDateRange(rows, startDate, endDate) {
  if (!startDate && !endDate) {
    return rows; // No filter applied
  }

  // Keep first 2 rows (headers)
  if (rows.length <= 2) return rows;
  
  const headers = rows.slice(0, 2);
  const dataRows = rows.slice(2);

  const filteredDataRows = dataRows.filter(row => {
    // Date is in the first column (row[0])
    const dateLabel = Array.isArray(row) ? row[0] : (row.date || row.label || row.week);
    const rowDate = parseDateFromRowLabel(dateLabel);
    
    if (!rowDate) return true; // Keep rows without parseable dates (like totals)
    
    if (startDate && rowDate < startDate) return false;
    if (endDate && rowDate > endDate) return false;
    
    return true;
  });

  return [...headers, ...filteredDataRows];
}

function ResultsDashboard({
  user,
  isPro,
  isMax,
  isAdmin,
  isVectorAdminPlus = false,
  authLoading,
  tierReady = true,
  showBetaEndMessage = false,
  onUpgrade,
  onNavigate,
  sheetNames,
  internalSheetNames = [],
  sheetLabels = {},
  loadingSheets,
  error: sheetError,
  view = 'vector',
  customCombos = [],
  customCombosLoaded = false,
  onCustomComboUpsert,
  onCustomComboRemove,
  isAdminActual = false,
  adminViewAs = 'admin',
}) {
  const { appName } = useBrandIdentity();
  // Brand Admin and up (as currently viewing, so "view as member" hides it).
  const { isVectorAdmin: viewerIsBrandAdmin } = useAuth();
  const canArrangeTabs = view === 'vector' && (isAdmin || viewerIsBrandAdmin);
  const [arrangeOpen, setArrangeOpen] = useState(false);
  // An old Sheet Manager link lands here with the panel open. The flag stays
  // until the panel is closed, since Results can mount more than once while
  // it loads.
  useEffect(() => {
    if (!canArrangeTabs) return;
    try {
      if (window.sessionStorage.getItem(OPEN_ARRANGE_KEY)) setArrangeOpen(true);
    } catch (_e) { /* storage blocked: the button still works */ }
  }, [canArrangeTabs]);
  const closeArrange = useCallback(() => {
    setArrangeOpen(false);
    try { window.sessionStorage.removeItem(OPEN_ARRANGE_KEY); } catch (_e) { /* nothing to clear */ }
  }, []);
  const vectorApiHeaders = useCallback(
    () => getVectorApiHeaders({ isAdminActual, adminViewAs }),
    [isAdminActual, adminViewAs],
  );
  const [rows, setRows] = useState([]);
  const [sheetOptions, setSheetOptions] = useState(['Current Results']);
  const [selectedSheet, setSelectedSheet] = useState(() => localStorage.getItem('results.selectedSheet') || 'Current Results');
  const [strategySheetRows, setStrategySheetRows] = useState([]);

  // Pro-only user-defined Multi-Strat combos. Each becomes a tab whose
  // `selectedSheet` value is `custom:<id>`; clicking it triggers a fetch
  // against /api/custom-multistrats/:id/data which returns combined rows.
  // State lives in App.js so risk tools and dashboards update without refresh.
  const [builderState, setBuilderState] = useState({ open: false, editing: null });
  const [builderSaving, setBuilderSaving] = useState(false);
  const [builderError, setBuilderError] = useState(null);
  const [loading, setLoading] = useState(true);
  const showVectorLoadingSkeleton = useDelayedTrueWhile(loading, VECTOR_RESULTS_LOADING_UI_DELAY_MS);
  const tierBlocking = !tierReady;
  const showTierWaitUi = useDelayedTrueWhile(tierBlocking, TIER_WAIT_UI_DELAY_MS);
  const ninjaAccessBlocking = authLoading || !tierReady;
  const myResultsAccess = hasMyResultsAccess({ isAdmin });
  const showNinjaAccessWaitUi = useDelayedTrueWhile(ninjaAccessBlocking, TIER_WAIT_UI_DELAY_MS);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const [search, setSearch] = useState(() => localStorage.getItem('results.search') || '');
  const [showLosersOnly, setShowLosersOnly] = useState(() => localStorage.getItem('results.showLosersOnly') === 'true');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('results.sortBy') || 'priority');
  const [category, setCategory] = useState(() => {
    const stored = localStorage.getItem('results.category');
    return stored && stored !== 'null' ? stored : null;
  });
  const [contractType, setContractType] = useState(() => localStorage.getItem('results.contractType') || 'NQ');
  const [contractSize, setContractSize] = useState(() => {
    const stored = localStorage.getItem('results.contractSize');
    return stored ? parseInt(stored, 10) : 1;
  });
  const [instrumentMap, setInstrumentMap] = useState({});
  
  // Dashboard view comes from the route — Vector Results and My Results are
  // distinct sidebar destinations and no longer share an in-page tab strip.
  const dashboardView = view === 'ninjatrader' ? 'ninjatrader' : 'vector';
  const isVectorNtTest = false;
  
  // My Results: filter which platform(s) appear in the unified account list (persisted)
  const [myResultsAccountPlatform, setMyResultsAccountPlatform] = useState(() => {
    const next = localStorage.getItem('results.myResultsAccountPlatform');
    if (next) return next;
    const legacy = localStorage.getItem('results.myResultsPlatform');
    if (legacy === 'tradovate') return 'tradovate';
    return 'all';
  });

  // NinjaTrader view: 'history' (My Trades), 'current', 'risk', or 'summary' — default to My Trades
  const [ninjatraderView, setNinjatraderView] = useState(() => localStorage.getItem('results.ninjatraderView') || 'history');
  
  // NinjaTrader card/table view mode for Summary
  const [ntCardViewMode, setNtCardViewMode] = useState(() => localStorage.getItem('results.ntCardViewMode') || 'table');
  
  const ninjaTraderDashboardRef = useRef(null);
  const [ntAccountsRefreshing, setNtAccountsRefreshing] = useState(false);

  // Sheet picker popover (icon dropdown beside the horizontal sheet tab strip)
  const [sheetPickerOpen, setSheetPickerOpen] = useState(false);
  const sheetPickerRef = useRef(null);
  const sheetTabsRef = useRef(null);
  const [sheetTabsScrollHintRight, setSheetTabsScrollHintRight] = useState(false);

  const updateSheetTabsScrollHint = useCallback(() => {
    const el = sheetTabsRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const hasOverflow = maxScroll > 6;
    const atEnd = el.scrollLeft >= maxScroll - 6;
    setSheetTabsScrollHintRight(hasOverflow && !atEnd);
  }, []);

  // Current Results "Spreadsheet" view + sort state (mirrors the Spreadsheet
  // tab inside StrategyDetailsView so users get the same experience).
  const [currentSpreadsheetSort, setCurrentSpreadsheetSort] = useState({ column: null, direction: null });

  // Past Results: read-only mirror of the Vector "Past Results" tab. Loaded
  // from the results-spreadsheet API on demand and rendered with the same
  // SpreadsheetView used elsewhere (sortable headers + classified rows).
  const [pastResultsRows, setPastResultsRows] = useState([]);
  const [pastResultsLoading, setPastResultsLoading] = useState(false);
  const [pastResultsError, setPastResultsError] = useState(null);
  const [pastResultsSort, setPastResultsSort] = useState({ column: null, direction: null });
  
  // Date range filter state
  const [dateRangeFilter, setDateRangeFilter] = useState(() => {
    const stored = localStorage.getItem('results.dateRangeFilter');
    return stored || 'all-time';
  });
  const [customStartDate, setCustomStartDate] = useState(() => {
    return localStorage.getItem('results.customStartDate') || '';
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    return localStorage.getItem('results.customEndDate') || '';
  });
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [pnlScalingModalOpen, setPnlScalingModalOpen] = useState(false);
  // Inline data-notice popover (full disclaimer shown on info-icon click)
  const [dataNoticeOpen, setDataNoticeOpen] = useState(false);
  // On narrow screens the filter bar collapses to a single button + popover
  // (TakeProfitTrader-style) instead of wrapping under the title.
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);

  const [displayFilter, setDisplayFilter] = useState(() => ({
    ...DEFAULT_DISPLAY_FILTER,
    days: { ...DEFAULT_DISPLAY_FILTER.days },
  }));
  const [mergedHighImpactByDate, setMergedHighImpactByDate] = useState(null);

  const [rollIndicatorsOn, setRollIndicatorsOn] = useState(readRollIndicatorsFromStorage);

  useEffect(() => {
    const onChange = () => setRollIndicatorsOn(readRollIndicatorsFromStorage());
    window.addEventListener('risklo-roll-indicators-changed', onChange);
    return () => window.removeEventListener('risklo-roll-indicators-changed', onChange);
  }, []);

  // Close the sheet picker on outside click or Escape so it behaves like a
  // standard popover (matches the Results Spreadsheet picker pattern).
  useEffect(() => {
    if (!sheetPickerOpen) return undefined;
    const handlePointer = (e) => {
      if (sheetPickerRef.current && !sheetPickerRef.current.contains(e.target)) {
        setSheetPickerOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setSheetPickerOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [sheetPickerOpen]);

  // Keep the active tab visible when the user changes sheet from the picker.
  useEffect(() => {
    if (!sheetTabsRef.current) return;
    const activeEl = sheetTabsRef.current.querySelector('.results-sheet-tab.active');
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    updateSheetTabsScrollHint();
  }, [selectedSheet, updateSheetTabsScrollHint]);

  // Show a subtle right-edge hint when more sheet tabs are off-screen.
  useEffect(() => {
    if (dashboardView !== 'vector') return undefined;
    const el = sheetTabsRef.current;
    if (!el) return undefined;

    updateSheetTabsScrollHint();
    el.addEventListener('scroll', updateSheetTabsScrollHint, { passive: true });
    window.addEventListener('resize', updateSheetTabsScrollHint);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateSheetTabsScrollHint);
      ro.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', updateSheetTabsScrollHint);
      window.removeEventListener('resize', updateSheetTabsScrollHint);
      if (ro) ro.disconnect();
    };
  }, [dashboardView, updateSheetTabsScrollHint, sheetOptions, customCombos.length]);

  // My Trades date range (separate from Vector Results)
  const [mthDateRangeFilter, setMthDateRangeFilter] = useState(() => {
    try {
      return localStorage.getItem('mth.dateRangeFilter') || 'all-time';
    } catch (_) { return 'all-time'; }
  });
  const [mthCustomStartDate, setMthCustomStartDate] = useState(() => {
    try {
      return localStorage.getItem('mth.customStartDate') || '';
    } catch (_) { return ''; }
  });
  const [mthCustomEndDate, setMthCustomEndDate] = useState(() => {
    try {
      return localStorage.getItem('mth.customEndDate') || '';
    } catch (_) { return ''; }
  });
  const [mthShowCustomDateModal, setMthShowCustomDateModal] = useState(false);

  // My Trades: include inactive accounts in dropdown (default: active only)
  const [mthIncludeInactiveAccounts, setMthIncludeInactiveAccounts] = useState(() => {
    try { return localStorage.getItem('mth.includeInactive') === 'true'; } catch (_) { return false; }
  });

  // My Trades account filter: 'all' or array of account names
  const [mthSelectedAccounts, setMthSelectedAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem('mth.selectedAccounts');
      if (!stored) return 'all';
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : 'all';
    } catch (_) { return 'all'; }
  });
  const [mthAccounts, setMthAccounts] = useState([]);
  const [mthAccountsLoading, setMthAccountsLoading] = useState(false);

  // Persist selections / filters
  useEffect(() => {
    localStorage.setItem('results.selectedSheet', selectedSheet);
    
    // Reset contract settings to defaults when Summary is selected
    if (selectedSheet === 'Summary') {
      setContractType('NQ');
      setContractSize(1);
    }
  }, [selectedSheet]);

  useEffect(() => {
    localStorage.setItem('results.search', search);
  }, [search]);

  useEffect(() => {
    localStorage.setItem('results.sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('results.category', category ?? 'null');
  }, [category]);

  useEffect(() => {
    localStorage.setItem('results.showLosersOnly', showLosersOnly);
  }, [showLosersOnly]);

  useEffect(() => {
    localStorage.setItem('results.contractType', contractType);
  }, [contractType]);

  useEffect(() => {
    // Only save to localStorage if it's a valid number
    if (contractSize !== '' && !isNaN(contractSize)) {
      localStorage.setItem('results.contractSize', contractSize.toString());
    }
  }, [contractSize]);

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.strategyInstruments, {
      headers: { ...getBearerAuthHeaders(), ...vectorApiHeaders() },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load instruments'))))
      .then((data) => {
        if (!cancelled && data?.success) setInstrumentMap(data.instruments || {});
      })
      .catch(() => {
        if (!cancelled) setInstrumentMap({});
      });
    return () => { cancelled = true; };
  }, [vectorApiHeaders]);

  useEffect(() => {
    localStorage.setItem('results.dateRangeFilter', dateRangeFilter);
  }, [dateRangeFilter]);

  useEffect(() => {
    localStorage.setItem('results.customStartDate', customStartDate);
  }, [customStartDate]);

  useEffect(() => {
    localStorage.setItem('results.customEndDate', customEndDate);
  }, [customEndDate]);

  useEffect(() => {
    localStorage.setItem('results.myResultsAccountPlatform', myResultsAccountPlatform);
  }, [myResultsAccountPlatform]);

  useEffect(() => {
    try {
      localStorage.setItem('mth.selectedAccounts', mthSelectedAccounts === 'all' ? '[]' : JSON.stringify(mthSelectedAccounts));
    } catch (_) {}
  }, [mthSelectedAccounts]);

  useEffect(() => {
    try { localStorage.setItem('mth.includeInactive', mthIncludeInactiveAccounts ? 'true' : 'false'); } catch (_) {}
  }, [mthIncludeInactiveAccounts]);

  // When hiding inactive accounts, reset selection if the selected account is inactive
  useEffect(() => {
    if (!mthIncludeInactiveAccounts && mthSelectedAccounts !== 'all') {
      const activeNames = new Set(mthAccounts.filter(a => a.is_active !== false).map(a => a.account_name));
      const selected = mthSelectedAccounts?.[0];
      if (selected && !activeNames.has(selected)) setMthSelectedAccounts('all');
    }
  }, [mthIncludeInactiveAccounts, mthAccounts, mthSelectedAccounts]);

  // Fetch accounts for My Trades account dropdown when on ninjatrader + history
  useEffect(() => {
    if (dashboardView !== 'ninjatrader' || ninjatraderView !== 'history' || !user?.email) return;
    let cancelled = false;
    setMthAccountsLoading(true);
    fetch(`${API_BASE_URL}/api/ninjatrader/accounts?userEmail=${encodeURIComponent(user.email)}`, {
      headers: getBearerAuthHeaders(),
    })
      .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch accounts')))
      .then(data => {
        if (!cancelled) setMthAccounts(data.accounts || []);
      })
      .catch(() => { if (!cancelled) setMthAccounts([]); })
      .finally(() => { if (!cancelled) setMthAccountsLoading(false); });
    return () => { cancelled = true; };
  }, [dashboardView, ninjatraderView, user?.email]);

  useEffect(() => {
    localStorage.setItem('results.ninjatraderView', ninjatraderView);
  }, [ninjatraderView]);

  useEffect(() => {
    localStorage.setItem('results.ntCardViewMode', ntCardViewMode);
  }, [ntCardViewMode]);

  useEffect(() => {
    try {
      localStorage.setItem('mth.dateRangeFilter', mthDateRangeFilter);
    } catch (_) {}
  }, [mthDateRangeFilter]);

  useEffect(() => {
    try {
      localStorage.setItem('mth.customStartDate', mthCustomStartDate);
    } catch (_) {}
  }, [mthCustomStartDate]);

  useEffect(() => {
    try {
      localStorage.setItem('mth.customEndDate', mthCustomEndDate);
    } catch (_) {}
  }, [mthCustomEndDate]);

  // Past Results — fetch lazily from the read-only results-spreadsheet API
  // (the same endpoint used by Results Spreadsheet → "Past results" tab).
  useEffect(() => {
    if (selectedSheet !== 'Past Results') return undefined;
    if (!user?.email) {
      setPastResultsRows([]);
      setPastResultsError(null);
      return undefined;
    }
    let cancelled = false;
    setPastResultsLoading(true);
    setPastResultsError(null);
    const headers = getBearerAuthHeaders();
    fetch(API_ENDPOINTS.resultsSpreadsheetData('Past Results'), { headers })
      .then(async (resp) => {
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data?.success) {
          throw new Error(userFacingStrategyFetchError(data?.error || `Failed to load Past Results (HTTP ${resp.status})`));
        }
        setPastResultsRows(Array.isArray(data.rows) ? data.rows : []);
        setPastResultsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPastResultsError(userFacingStrategyFetchError(err.message || 'Failed to load Past Results'));
        setPastResultsRows([]);
      })
      .finally(() => {
        if (!cancelled) setPastResultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSheet, user?.email]);

  // Update sheet options when sheetNames changes from App.js
  useEffect(() => {
    if (sheetNames && sheetNames.length > 0) {
      // Filter out sample/current/summary variants from backend list; these are added manually.
      const filteredSheets = sheetNames.filter(
        (name) => {
          const normalized = (name || '').trim().toLowerCase();
          if (!normalized) return false;
          if (normalized === 'sample strategy (demo)') return false;
          if (normalized === 'summary') return false;
          if (normalized.includes('current results')) return false;
          return true;
        }
      );
      // preserveOrder: sheetNames already arrives in the admin Sheet Manager
      // order from /api/sheets — keep it rather than re-applying the legacy
      // hardcoded priority list (which would override the admin's ordering).
      const sortedSheets = sortStrategies(filteredSheets, { preserveOrder: true });

      // Strip the virtual Multi-Strat (pinned in the head below) and custom
      // combos (rendered as their own dedicated tabs via the customCombos.map
      // block) so they don't reappear in the tail. Every real strategy sheet
      // keeps the admin Sheet Manager order from /api/sheets — no hardcoded
      // hoisting, so reordering in the Sheet Manager is reflected here too.
      const includesMasterStrat = sortedSheets.some(isMultiStratName);
      const remainingSheets = sortedSheets.filter(
        (name) => !isMultiStratName(name)
          && !(typeof name === 'string' && name.startsWith('custom:')),
      );

      // Head = the collective/system tabs. Those are views OVER the strategy
      // set rather than strategies themselves, so they stay even when the set
      // is small — with two exceptions:
      //
      //  • Current/Past Results read Vector's Google Results Spreadsheet, so
      //    they are Vector-only; another brand would get tabs that can only
      //    ever be empty.
      //  • The Master Strat is injected into sheetNames by App.js ONLY when
      //    this brand has an admin-configured composition. Do not infer it from
      //    "has ≥1 strategy" — that surfaced a phantom "<Brand> Master Strat"
      //    tab on every brand that never configured one.
      //
      // Everything after the head is a real strategy, and comes straight from
      // Strategy Data (/api/sheets reads the active snapshots for this brand).
      // No strategy in Strategy Data means no tab — for every brand, not just
      // new ones.
      const head = [
        ...(isVectorEntity() ? ['Current Results', 'Past Results'] : []),
        'Summary',
        ...(includesMasterStrat ? [MULTI_STRAT_SHEET_NAME] : []),
      ];
      setSheetOptions([...head, ...remainingSheets]);
    }
  }, [sheetNames]);

  // Self-heal a stale `selectedSheet` like `custom:<id>` once combos load —
  // if the combo no longer exists (deleted in another session, or tier
  // dropped to Basic), fall back to the built-in Multi-Strat so the page
  // doesn't get stuck trying to fetch a missing combo.
  useEffect(() => {
    if (!customCombosLoaded) return;
    if (typeof selectedSheet !== 'string') return;
    if (!selectedSheet.startsWith('custom:')) return;
    const id = selectedSheet.slice('custom:'.length);
    if (!customCombos.some((c) => c.id === id)) {
      // Clearing to null lets the "snap to an existing tab" effect below pick
      // the first tab this brand actually has — the Master Strat tab is absent
      // when there are no strategies to combine, so it can't be hardcoded here.
      setSelectedSheet(null);
    }
  }, [customCombosLoaded, customCombos, selectedSheet]);

  // Snap the selection to something that actually exists.
  //
  // `selectedSheet` is restored from localStorage and defaults to
  // 'Current Results', but the available tabs now depend on the brand and on
  // what is in Strategy Data — so a remembered tab (or the default) can point
  // at something this brand does not have. Without this the page fetches a
  // sheet that isn't there and renders an error instead of just showing the
  // first real tab.
  useEffect(() => {
    if (!sheetOptions.length) return;
    // Custom combos are rendered as their own tabs and are not in sheetOptions.
    if (typeof selectedSheet === 'string' && selectedSheet.startsWith('custom:')) return;
    if (sheetOptions.includes(selectedSheet)) return;
    setSelectedSheet(sheetOptions[0]);
  }, [sheetOptions, selectedSheet]);

  // Pass through error from App.js (skip while auth is still hydrating to avoid a 401 flash)
  useEffect(() => {
    if (authLoading) return;
    if (sheetError) {
      setError(sheetError);
      return;
    }
    setError((prev) => {
      // Clear a stale membership denial regardless of which brand it named —
      // matching on the literal word "Vector" left the banner stuck on every
      // other brand.
      if (prev && isMembershipDenialText(prev)) return null;
      return prev;
    });
  }, [sheetError, authLoading]);

  useEffect(() => {
    if (authLoading) return;

    const email = (user?.email && String(user.email).trim()) || getStoredRiskloUserEmail();
    // Presence check only — request headers come from getBearerAuthHeaders(),
    // which also carries the active-entity header.
    const token = getRiskloAuthToken();

    if (token && !email) return;

    if (!email && !token) {
      setLoading(false);
      setError(null);
      setRows([]);
      setStrategySheetRows([]);
      return;
    }

    if (!tierReady) {
      return;
    }

    // We used to bail here when !isPro and show empty rows everywhere. The
    // page now blurs only the KPI summary block for Basic users (via the
    // overlay above the cards) — the calendar, weekly summary, chart, and
    // spreadsheet sections render real data for any signed-in user. The
    // server's verifyWhopMembership middleware is still the source of truth
    // for who can read; a Basic user without Vector access gets a 403 from
    // the API and the section renders its own empty state.

    const fetchResults = async () => {
      // Past Results uses its own loader; clear the strategy-sheet state and
      // bail so we don't accidentally hit /strategy-sheet for that name.
      if (selectedSheet === 'Past Results') {
        setRows([]);
        setStrategySheetRows([]);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const authHeaders = vectorApiHeaders();
      try {
        if (selectedSheet === 'Current Results') {
          const crUrl = isVectorNtTest
            ? API_ENDPOINTS.vectorNtCurrentResults(email)
            : API_ENDPOINTS.currentResults(email);
          const resp = await fetch(crUrl, { headers: authHeaders });
          const contentType = resp.headers.get('content-type') || '';
          const isJson = contentType.toLowerCase().includes('application/json');
          const data = isJson ? await resp.json() : { success: false, error: await resp.text() };

          if (!resp.ok && isMembershipAccessDenied(resp, data)) {
            setRows([]);
            setStrategySheetRows([]);
            setError(membershipDeniedMessage(data));
            setLoading(false);
            return;
          }

          if (!resp.ok && resp.status === 403 && data.requiresAdmin) {
            setRows([]);
            setStrategySheetRows([]);
            setError(data.error || 'Admin access is required.');
            setLoading(false);
            return;
          }

          if (!data.success) {
            throw new Error(userFacingCurrentResultsError(data.error || 'Failed to load Current Results'));
          }

          setError(null);
          setRows(Array.isArray(data.rows) ? data.rows : []);
          setStrategySheetRows([]);
          return;
        }

        if (selectedSheet === 'Summary') {
          // For Summary, we don't fetch any specific sheet - StrategySummaryTable handles its own fetch
          setError(null);
          setRows([]);
          setStrategySheetRows([]);
          setLoading(false);
          return;
        }

        // Custom Multi-Strat combo: server returns the already-combined rows.
        if (typeof selectedSheet === 'string' && selectedSheet.startsWith('custom:')) {
          const comboId = selectedSheet.slice('custom:'.length);
          if (!comboId) {
            setRows([]);
            setStrategySheetRows([]);
            setError('Combo not found.');
            setLoading(false);
            return;
          }
          const resp = await fetch(API_ENDPOINTS.customMultistratData(comboId), { headers: authHeaders });
          const ct = resp.headers.get('content-type') || '';
          const isJson = ct.toLowerCase().includes('application/json');
          const data = isJson ? await resp.json() : { success: false, error: await resp.text() };
          if (!resp.ok || !data?.success) {
            throw new Error(userFacingStrategyFetchError(data?.error || `Failed to load combo (${resp.status})`));
          }
          setError(null);
          setStrategySheetRows(Array.isArray(data.rows) ? data.rows : []);
          setRows([]);
          setLoading(false);
          return;
        }

        if (isMultiStratName(selectedSheet)) {
          // Vector Master Strat is now an admin-editable org-wide combo. The
          // server fans out to its configured source sheets and returns the
          // already-combined rows, so the client just fetches it like any sheet
          // (no hardcoded source list here anymore).
          const masterUrl = isVectorNtTest
            ? API_ENDPOINTS.vectorNtStrategySheet(MULTI_STRAT_SHEET_NAME, email)
            : API_ENDPOINTS.strategySheet(MULTI_STRAT_SHEET_NAME, email);
          const resp = await fetch(masterUrl, { headers: authHeaders });
          const ct = resp.headers.get('content-type') || '';
          const isJson = ct.toLowerCase().includes('application/json');
          const data = isJson ? await resp.json() : { success: false, error: await resp.text() };
          if (!resp.ok && isMembershipAccessDenied(resp, data)) {
            setRows([]);
            setStrategySheetRows([]);
            setError(membershipDeniedMessage(data));
            setLoading(false);
            return;
          }
          if (!resp.ok && resp.status === 403 && data.requiresAdmin) {
            setRows([]);
            setStrategySheetRows([]);
            setError(data.error || 'Admin access is required.');
            setLoading(false);
            return;
          }
          if (!resp.ok || !data.success) {
            throw new Error(userFacingStrategyFetchError(data.error || `Failed to load ${MULTI_STRAT_SHEET_NAME}`));
          }
          setError(null);
          setStrategySheetRows(Array.isArray(data.rows) ? data.rows : []);
          setRows([]);
          setLoading(false);
          return;
        }

        const sheetUrl = isVectorNtTest
          ? API_ENDPOINTS.vectorNtStrategySheet(selectedSheet, email)
          : API_ENDPOINTS.strategySheet(selectedSheet, email);
        const resp = await fetch(sheetUrl, { headers: authHeaders });
        const contentType = resp.headers.get('content-type') || '';
        const isJson = contentType.toLowerCase().includes('application/json');
        const data = isJson ? await resp.json() : { success: false, error: await resp.text() };

        if (!resp.ok && isMembershipAccessDenied(resp, data)) {
          setRows([]);
          setStrategySheetRows([]);
          setError(membershipDeniedMessage(data));
          setLoading(false);
          return;
        }

        if (!resp.ok && resp.status === 403 && data.requiresAdmin) {
          setRows([]);
          setStrategySheetRows([]);
          setError(data.error || 'Admin access is required.');
          setLoading(false);
          return;
        }

        if (!data.success) {
          throw new Error(userFacingStrategyFetchError(data.error || 'Failed to load sheet'));
        }

        setError(null);
        // Store raw rows without filtering - filtering will be done in useMemo
        setStrategySheetRows(Array.isArray(data.rows) ? data.rows : []);
        setRows([]);
      } catch (err) {
        setError(userFacingStrategyFetchError(err.message || 'Failed to load Current Results'));
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [user, selectedSheet, authLoading, isPro, tierReady, isVectorNtTest, isAdmin, vectorApiHeaders]);

  // Live / Backtested / Full Journey: strategies with a "live since" date
  // (Strategies → Live data), keyed by their data (sheet) name.
  const [liveSinceBySheet, setLiveSinceBySheet] = useState({});
  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.vectorScenariosEnabled, { headers: { ...getBearerAuthHeaders(), ...vectorApiHeaders() } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.success) return;
        const map = {};
        (data.scenarios || []).forEach((s) => {
          const live = normalizeLiveSince(s?.dataSourceConfig?.liveSince);
          const name = String(s?.dataSourceConfig?.strategyName || '').trim().toLowerCase();
          if (live && name) map[name] = live;
        });
        setLiveSinceBySheet(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vectorApiHeaders]);
  const liveSince = liveSinceBySheet[String(selectedSheet || '').trim().toLowerCase()] || null;
  const [journeyView, setJourneyView] = usePersistentTab('results.journey', 'live', ['live', 'backtested', 'full']);
  const journeyRows = useMemo(
    () => filterSheetRowsByJourney(strategySheetRows, liveSince, journeyView),
    [strategySheetRows, liveSince, journeyView],
  );

  // Apply date range filter to strategy sheet data (only for historical strategies, not Current Results)
  const filteredStrategySheetRows = useMemo(() => {
    if (selectedSheet === 'Current Results' || journeyRows.length === 0) {
      return journeyRows;
    }
    
    const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
    return filterRowsByDateRange(journeyRows, startDate, endDate);
  }, [journeyRows, dateRangeFilter, customStartDate, customEndDate, selectedSheet]);

  const needsMergedImpact = useMemo(
    () =>
      isDisplayFilterActive(displayFilter) &&
      RED_FOLDER_FILTER_ENABLED && (displayFilter.redFolderOnly || displayFilter.nonRedFolderOnly),
    [displayFilter],
  );

  useEffect(() => {
    if (!needsMergedImpact) {
      setMergedHighImpactByDate(null);
      return undefined;
    }
    setMergedHighImpactByDate(null);
    const months = uniqueMonthKeysFromStrategyRows(filteredStrategySheetRows);
    if (months.length === 0) {
      setMergedHighImpactByDate(new Map());
      return undefined;
    }
    let cancelled = false;
    Promise.all(months.map((mk) => loadUsHighImpactEvents(mk, ['ff-red']))).then((maps) => {
      if (cancelled) return;
      const merged = new Map();
      for (const m of maps) {
        m.forEach((arr, k) => merged.set(k, arr));
      }
      setMergedHighImpactByDate(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [needsMergedImpact, filteredStrategySheetRows]);

  // Compute metrics from filtered data (display filter applies to summary cards + calendar)
  const strategyMetrics = useMemo(() => {
    if (filteredStrategySheetRows.length === 0) return null;
    const base = computeStrategySheetMetrics(filteredStrategySheetRows);

    if (!isDisplayFilterActive(displayFilter)) return base;

    if (needsMergedImpact && mergedHighImpactByDate === null) return base;

    const impactMap = needsMergedImpact ? mergedHighImpactByDate || new Map() : new Map();
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);

    const includeDay = ({ dateKey, weekdayLabel }) => {
      const p = (dateKey || '').split('-');
      if (p.length !== 3) return false;
      const y = parseInt(p[0], 10);
      const mo = parseInt(p[1], 10) - 1;
      const d = parseInt(p[2], 10);
      if (isNaN(y) || isNaN(mo) || isNaN(d)) return false;
      const rollInfo = rollFilterEnabled
        ? getNasdaqRollWeekInfoForDay(y, mo, d)
        : { inRollWeek: false };
      return dayPassesDisplayFilter(dateKey, weekdayLabel, displayFilter, impactMap, {
        rollFilterEnabled,
        inRollWeek: !!rollInfo.inRollWeek,
      });
    };

    return computeStrategySheetMetrics(filteredStrategySheetRows, { includeDay });
  }, [
    filteredStrategySheetRows,
    displayFilter,
    needsMergedImpact,
    mergedHighImpactByDate,
    contractType,
  ]);

  /** Same date range as strategyMetrics, without display filter — for "vs … all" comparisons */
  const strategyMetricsBaseline = useMemo(() => {
    if (filteredStrategySheetRows.length === 0) return null;
    return computeStrategySheetMetrics(filteredStrategySheetRows);
  }, [filteredStrategySheetRows]);

  const model = useMemo(() => transformSheetToResultsDashboardModel(rows), [rows]);

  const allStrategies = useMemo(() => model.groups.flatMap((g) => g.strategies), [model.groups]);
  const categoryOptions = useMemo(() => {
    const set = new Set(model.groups.map((g) => g.name));
    return Array.from(set).sort();
  }, [model.groups]);

  const filteredSortedGroups = useMemo(() => {
    const matchSearch = (name) =>
      !search || name.toLowerCase().includes(search.trim().toLowerCase());

    const norm = (s) => (s || '').toString().trim().toLowerCase();

    const strategies = allStrategies
      .filter((s) => matchSearch(s.strategyName))
      .filter((s) => (showLosersOnly ? s.weeklyTotal < 0 : true))
      .filter((s) => {
        if (!category) return true;
        const groupName = model.groupByStrategy.get(s.strategyName) || 'Uncategorized';
        return norm(groupName) === norm(category);
      });

    const withComputed = strategies.map((s) => {
      const dayValues = Object.values(s.dailyPnL || {});
      return {
        ...s,
        worstDay: Math.min(...dayValues),
        positiveCount: dayValues.filter((v) => v > 0).length,
      };
    });

    if (sortBy === 'priority') {
      const orderedNames = sortStrategies(withComputed.map((s) => s.strategyName));
      const orderMap = new Map();
      orderedNames.forEach((n, i) => orderMap.set(n, i));
      withComputed.sort((a, b) => {
        const ao = orderMap.has(a.strategyName) ? orderMap.get(a.strategyName) : 999;
        const bo = orderMap.has(b.strategyName) ? orderMap.get(b.strategyName) : 999;
        if (ao !== bo) return ao - bo;
        return b.weeklyTotal - a.weeklyTotal;
      });
    } else switch (sortBy) {
      case 'totalAsc':
        withComputed.sort((a, b) => a.weeklyTotal - b.weeklyTotal);
        break;
      case 'worstDayDesc':
        withComputed.sort((a, b) => (a.worstDay || 0) - (b.worstDay || 0));
        break;
      case 'consistencyDesc':
        withComputed.sort((a, b) => (b.positiveCount || 0) - (a.positiveCount || 0));
        break;
      case 'totalDesc':
      default:
        withComputed.sort((a, b) => b.weeklyTotal - a.weeklyTotal);
        break;
    }

    // Rebuild into groups using name->group map
    const groupsMap = new Map();
    for (const s of withComputed) {
      const key = model.groupByStrategy.get(s.strategyName) || 'Uncategorized';
      if (!groupsMap.has(key)) groupsMap.set(key, []);
      groupsMap.get(key).push(s);
    }

    return Array.from(groupsMap.entries())
      .map(([name, strategies]) => ({ name, strategies }))
      .filter((g) => g.strategies.length > 0);
  }, [allStrategies, model.groupByStrategy, search, showLosersOnly, sortBy, category]);

  const isCurrent = selectedSheet === 'Current Results';
  const isSummary = selectedSheet === 'Summary';
  const isMultiStrat = isMultiStratName(selectedSheet);
  const isPastResults = selectedSheet === 'Past Results';
  const isCustomCombo = typeof selectedSheet === 'string' && selectedSheet.startsWith('custom:');
  const activeCustomCombo = useMemo(() => {
    if (!isCustomCombo) return null;
    const id = selectedSheet.slice('custom:'.length);
    return customCombos.find((c) => c.id === id) || null;
  }, [isCustomCombo, selectedSheet, customCombos]);
  // Display-friendly sheet name (header / titles use this rather than the raw
  // `custom:<id>` value so combos appear as their user-given names).
  const displaySheetName = isCustomCombo
    ? (activeCustomCombo?.name || 'Custom strat combo')
    : selectedSheet;
  // Internal (admin-only) strategies. `internalSheetNames` is always empty for
  // members, so these markers only ever render for admins: a green ring on the
  // tab, plus an explicit "Internal" badge beside the strategy name in the
  // page header and the Strategy Performance heading — glancing at a chart
  // should never leave you unsure whether members can see it too.
  const internalSheetSet = useMemo(
    () => new Set((internalSheetNames || []).map((n) => String(n).trim().toLowerCase())),
    [internalSheetNames],
  );
  const selectedSheetIsInternal = !isCustomCombo
    && internalSheetSet.has(String(selectedSheet || '').trim().toLowerCase());
  // Strategy Settings rename → show the new name. Tabs/pickers keep using the
  // raw data name as their value, so selection and data fetches are unchanged.
  const labelForSheet = useCallback(
    (name) => {
      if (sheetLabels && sheetLabels[name]) return sheetLabels[name];
      // MULTI_STRAT_SHEET_NAME is a DATA KEY (sent to /api/strategy-sheet and
      // stored in saved combos), so only its label follows the brand.
      if (name === MULTI_STRAT_SHEET_NAME) return `${getEntityDisplayName()} Master Strat`;
      return name;
    },
    [sheetLabels],
  );
  // Build a "Combined view: GFPB on Mon/Wed (1×), Tuesday Special on Tue (2×)"
  // sentence for the active custom combo. Mirrors the static description we
  // show for the built-in Multi-Strat so a viewer immediately understands
  // what the combo is composed of.
  const customComboDescription = useMemo(() => {
    const sleeves = Array.isArray(activeCustomCombo?.sleeves) ? activeCustomCombo.sleeves : [];
    if (sleeves.length === 0) return null;
    const dayShort = {
      monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri',
    };
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const parts = sleeves
      .filter((s) => s && typeof s.strategy === 'string' && Array.isArray(s.days) && s.days.length > 0)
      .map((s) => {
        const sortedDays = [...s.days].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
        const dayLabel = sortedDays.map((d) => dayShort[d] || d).join(' / ');
        const size = Number(s.size);
        const sizeLabel = Number.isFinite(size) && size !== 1 ? ` (${size}×)` : '';
        return `${s.strategy} on ${dayLabel}${sizeLabel}`;
      });
    if (parts.length === 0) return null;
    return `Combined view: ${parts.join(', ')}.`;
  }, [activeCustomCombo]);
  const contractTypeLabels = useMemo(() => {
    if (isCustomCombo && Array.isArray(activeCustomCombo?.sleeves)) {
      return getContractTypeLabelsForCombo(activeCustomCombo.sleeves);
    }
    const inst = resolveStrategyInstrument(selectedSheet, instrumentMap);
    const fromInstrument = contractTypeLabelsForInstrument(inst, selectedSheet);
    if (fromInstrument) return fromInstrument;
    return getContractTypeLabelsForStrategy(selectedSheet);
  }, [isCustomCombo, activeCustomCombo, selectedSheet, instrumentMap]);

  const showContractTypeToggle = useMemo(() => {
    if (isCurrent || isSummary) return true;
    if (isCustomCombo && Array.isArray(activeCustomCombo?.sleeves)) {
      return activeCustomCombo.sleeves.some((s) =>
        usesFuturesContractToggle(resolveStrategyInstrument(s?.strategy, instrumentMap)),
      );
    }
    return usesFuturesContractToggle(resolveStrategyInstrument(selectedSheet, instrumentMap));
  }, [isCurrent, isSummary, isCustomCombo, activeCustomCombo, selectedSheet, instrumentMap]);

  const isMixedContractFamilyCombo = useMemo(
    () => isCustomCombo && comboHasMixedContractFamilies(activeCustomCombo?.sleeves),
    [isCustomCombo, activeCustomCombo],
  );

  const showVectorFiltersReset = useMemo(() => {
    if (isCurrent) return contractType !== 'NQ' || contractSize !== 1;
    if (isSummary) return contractType !== 'NQ' || contractSize !== 1;
    return (
      contractType !== 'NQ' ||
      contractSize !== 1 ||
      dateRangeFilter !== 'all-time' ||
      isDisplayFilterActive(displayFilter)
    );
  }, [isCurrent, isSummary, contractType, contractSize, dateRangeFilter, displayFilter]);

  const handleSheetChange = (e) => {
    const newSheet = e.target.value;

    // Signed-out users still get bounced to login — they can't render any of
    // these views without an auth token. Basic users, on the other hand, can
    // navigate freely between Current Results / Past Results / Summary /
    // Multi-Strat / individual strategies; the KPI stats block inside each
    // strategy view is what's locked behind Pro (frosted blur + upgrade card).
    if (tierReady && newSheet !== 'Current Results' && !isPro && !signedIn) {
      if (onNavigate) onNavigate('login');
      return;
    }

    setSelectedSheet(newSheet);
    if (newSheet !== 'Current Results' && newSheet !== 'Summary') {
      trackEvent('strategy_opened', {
        strategy_name: newSheet,
        source: 'vector_results_dropdown',
      });
    }
  };

  /* ── Custom Multi-Strat: open/save/delete handlers ───────────────────── */

  const openCustomComboBuilder = (combo = null) => {
    if (!isPro) {
      if (typeof onUpgrade === 'function') {
        onUpgrade(`Upgrade to ${appName} Pro to build your own custom strat combos.`);
      }
      return;
    }
    setBuilderError(null);
    setBuilderState({ open: true, editing: combo });
  };

  const closeCustomComboBuilder = () => {
    if (builderSaving) return;
    setBuilderState({ open: false, editing: null });
    setBuilderError(null);
  };

  const handleSaveCustomCombo = async (payload) => {
    setBuilderSaving(true);
    setBuilderError(null);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getBearerAuthHeaders(),
      };
      const editing = builderState.editing;
      const url = editing
        ? API_ENDPOINTS.customMultistratById(editing.id)
        : API_ENDPOINTS.customMultistrats;
      const method = editing ? 'PUT' : 'POST';
      const resp = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || `Save failed (${resp.status})`);
      }
      // Refresh app-wide combo list so risk tools and dashboards update immediately.
      onCustomComboUpsert?.(data.combo);
      setBuilderSaving(false);
      setBuilderState({ open: false, editing: null });
      const newSheetValue = `custom:${data.combo.id}`;
      setSelectedSheet(newSheetValue);
      trackEvent(editing ? 'custom_multistrat_updated' : 'custom_multistrat_created', {
        combo_id: data.combo.id,
        sleeve_count: Array.isArray(data.combo?.sleeves) ? data.combo.sleeves.length : 0,
      });
    } catch (err) {
      setBuilderSaving(false);
      setBuilderError(err?.message || 'Failed to save combo');
    }
  };

  const handleDeleteCustomCombo = async () => {
    const editing = builderState.editing;
    if (!editing?.id) return;
    setBuilderSaving(true);
    setBuilderError(null);
    try {
      const headers = getBearerAuthHeaders();
      const resp = await fetch(API_ENDPOINTS.customMultistratById(editing.id), {
        method: 'DELETE',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || `Delete failed (${resp.status})`);
      }
      onCustomComboRemove?.(editing.id);
      setBuilderSaving(false);
      setBuilderState({ open: false, editing: null });
      // If the deleted combo was active, fall back to Multi-Strat.
      if (selectedSheet === `custom:${editing.id}`) {
        // null lets the "snap to an existing tab" effect choose the first tab
        // this brand actually has.
        setSelectedSheet(null);
      }
      trackEvent('custom_multistrat_deleted', { combo_id: editing.id });
    } catch (err) {
      setBuilderSaving(false);
      setBuilderError(err?.message || 'Failed to delete combo');
    }
  };

  const handleCurrentStrategyClick = (strategyName) => {
    if (!strategyName) return;
    setSelectedSheet(strategyName);
    trackEvent('strategy_opened', {
      strategy_name: strategyName,
      source: 'current_results_card',
    });
  };

  const signedIn = Boolean(user?.email && String(user.email).trim());

  const vectorFeatureGateStack =
    dashboardView === 'vector' &&
    tierReady &&
    ((signedIn && !isPro && !showBetaEndMessage) || (!signedIn && !authLoading));

  const ninjaFullPageGate =
    dashboardView === 'ninjatrader' &&
    !ninjaAccessBlocking &&
    ((!signedIn && !authLoading) || (signedIn && !myResultsAccess));

  const useFeatureGateStack = vectorFeatureGateStack || ninjaFullPageGate;

  const showCustomComboCta =
    dashboardView === 'vector'
    && tierReady
    && customCombosLoaded
    && (isPro || customCombos.length === 0);

  const renderCustomComboCta = () => {
    if (!showCustomComboCta) return null;
    if (customCombos.length < MAX_CUSTOM_COMBOS) {
      return (
        <button
          type="button"
          className={`results-sheet-combo-cta ${!isPro ? 'is-locked' : ''}`}
          onClick={() => openCustomComboBuilder(null)}
          title={isPro
            ? `Build your own strat combo (${customCombos.length}/${MAX_CUSTOM_COMBOS} used)`
            : `Upgrade to ${appName} Pro to build custom strat combos`}
        >
          {isPro
            ? <>+ Build your own strat combo <span className="results-sheet-combo-cta-count">{customCombos.length}/{MAX_CUSTOM_COMBOS}</span></>
            : '+ Custom strat combo (Pro)'}
        </button>
      );
    }
    return (
      <span
        className="results-sheet-combo-cap-pill"
        title="Edit a strat combo's pencil icon and delete it to free up a slot."
      >
        Strat combos: {MAX_CUSTOM_COMBOS}/{MAX_CUSTOM_COMBOS} used
      </span>
    );
  };

  return (
    <div className={`results-dash-page${useFeatureGateStack ? ' feature-gate-stack' : ''}`}>
      {dashboardView !== 'vector' && (
        <PageHeading title="My Results" description="Your imported trade history and performance." />
      )}

      {/* Access Denied Message */}
      {error && !authLoading && !error.includes('WRONG SPREADSHEET') && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Vector Results content (Google Sheets) */}
      {dashboardView === 'vector' && (
        !tierReady ? (
          showTierWaitUi ? (
            <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
              <div className="results-tier-placeholder-inner">
                <span className="results-tier-placeholder-spin" aria-hidden="true">
                  <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
                </span>
                <p className="results-tier-placeholder-text">Loading your account access…</p>
              </div>
            </div>
          ) : null
        ) : authLoading && !signedIn ? (
          <div className="results-tier-placeholder" role="status" aria-live="polite" aria-busy="true">
            <div className="results-tier-placeholder-inner">
              <span className="results-tier-placeholder-spin" aria-hidden="true">
                <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
              </span>
              <p className="results-tier-placeholder-text">Loading…</p>
            </div>
          </div>
        ) : !signedIn ? (
          <div className="upgrade-required-page">
            <div className="upgrade-required-content">
              <div className="pro-lock-icon">
                <IconLock size={48} />
              </div>
              <h2 className="pro-lock-title">{getResultsNavLabel()}</h2>
              <p className="pro-lock-subtitle">Sign in to continue</p>
              <p className="pro-lock-description">
                Sign in to view and analyze strategy performance data.
              </p>
              <button
                type="button"
                className="pro-lock-upgrade-button"
                onClick={() => onNavigate && onNavigate('login')}
              >
                Sign in
              </button>
            </div>
          </div>
        ) : !isPro && showBetaEndMessage ? (
          // Beta-ended users still see the legacy sample-preview overlay.
          // Outside that case, Basic users now fall through to the regular
          // Vector Results UI — the KPI stats block inside StrategyDetailsView
          // is blurred with a Pro upgrade overlay (see viewerIsPro prop).
          <div style={{ position: 'relative', minHeight: '400px' }}>
            <VectorResultsSamplePreview />
            <BetaEndedCard variant="overlay" />
          </div>
        ) : (
        <>
          {/* Wrong Spreadsheet Warning Banner */}
          {error && error.includes('WRONG SPREADSHEET') && (
            <div className="error-message" style={{ 
              marginBottom: '1.5rem',
              padding: '1rem 1.25rem',
              background: 'rgba(248, 113, 113, 0.15)',
              border: '2px solid rgba(248, 113, 113, 0.4)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: '1.05rem', display: 'block', marginBottom: '0.25rem' }}>WRONG SPREADSHEET</strong>
                <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                  {error.replace('WRONG SPREADSHEET: ', '')}
                </p>
              </div>
            </div>
          )}
          
          <div className="results-titlebar">
          <PageHeading
            title={getResultsNavLabel()}
            description="All strategy performance data, by strategy and weekday."
            className="results-titlebar-heading"
          />
          {!isPastResults && (
          <div className={`results-filters-open${filtersPopoverOpen ? ' is-popover-open' : ''}`}>
            <button
              type="button"
              className="results-filters-popover-toggle"
              onClick={() => setFiltersPopoverOpen((v) => !v)}
              aria-expanded={filtersPopoverOpen}
              aria-controls="results-vector-filters-panel"
            >
              <IconFilter size={16} />
              <span>Filters</span>
              <IconChevronDown size={14} />
            </button>
            <div className="results-controls-row" id="results-vector-filters-panel">
            <div className="results-vector-filters-row">
              {showContractTypeToggle ? (
              <div className="results-select-group">
                <label
                  className="results-select-label"
                  title={isMixedContractFamilyCombo
                    ? 'Reg / Micro scales each strategy\'s native contract (NQ, YM, ES, etc.)'
                    : undefined}
                >
                  Contract Type
                </label>
                <div className="results-contract-type-chips">
                {['NQ', 'MNQ'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`results-contract-type-chip ${contractType === type ? 'active' : ''}`}
                    onClick={() => setContractType(type)}
                    aria-label={`Select ${type === 'NQ' ? contractTypeLabels.full : contractTypeLabels.micro}`}
                    aria-pressed={contractType === type}
                    title={isMixedContractFamilyCombo && type === 'NQ'
                      ? 'Regular size — each strategy uses its native full contract'
                      : isMixedContractFamilyCombo && type === 'MNQ'
                        ? 'Micro size — each strategy scaled to its native micro contract'
                        : undefined}
                  >
                    {type === 'NQ' ? contractTypeLabels.full : contractTypeLabels.micro}
                  </button>
                ))}
              </div>
              </div>
              ) : null}
              <div className="results-select-group results-contract-size-group">
                <div className="results-contract-label-row">
                  <label htmlFor="contract-size-input" className="results-select-label" title="Contract multiplier — scales displayed PnL by your number of contracts (e.g. 3 = 3×)">
                    <span className="results-label-line">Contract Multi</span>
                  </label>
                  <button
                    type="button"
                    className="results-pnl-scale-info"
                    aria-haspopup="dialog"
                    aria-expanded={pnlScalingModalOpen}
                    aria-label="Open PnL scaling note"
                    onClick={() => setPnlScalingModalOpen(true)}
                  >
                    <IconInfo size={14} />
                  </button>
                  <PnlScalingInfoModal
                    open={pnlScalingModalOpen}
                    onClose={() => setPnlScalingModalOpen(false)}
                    paragraphs={DEFAULT_PNL_SCALING_PARAGRAPHS}
                  />
                </div>
              <input
                id="contract-size-input"
                type="number"
                className="results-contract-size-input"
                aria-label="Default Contract Size Multiplier (scales displayed PnL)"
                min="1"
                max="100"
                value={contractSize}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  
                  // Allow empty string (when user backspaces everything)
                  if (inputValue === '') {
                    setContractSize('');
                    return;
                  }
                  
                  const value = parseInt(inputValue, 10);
                  if (!isNaN(value) && value >= 1 && value <= 100) {
                    setContractSize(value);
                  }
                }}
                onBlur={(e) => {
                  // When user leaves the field, ensure it has a valid value
                  if (e.target.value === '' || parseInt(e.target.value, 10) < 1) {
                    setContractSize(1);
                  }
                }}
                placeholder="1"
              />
              </div>
              {!isCurrent && !isSummary && (
                <div className="results-select-group">
                  <label htmlFor="date-range-filter" className="results-select-label">
                  Date Range
                </label>
                <select
                  id="date-range-filter"
                  className="results-date-range-select"
                  aria-label="Select date range"
                  value={dateRangeFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'custom') {
                      setShowCustomDateModal(true);
                    } else {
                      setDateRangeFilter(value);
                    }
                  }}
                >
                  <option value="all-time">All Time</option>
                  <option value="last-30-days">Last 30 Days</option>
                  <option value="last-3-months">Last 3 Months</option>
                  <option value="last-6-months">Last 6 Months</option>
                  <option value="last-12-months">Last 12 Months</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="custom">Custom Range...</option>
                </select>
                </div>
              )}
              {((!isCurrent && !isSummary) || showVectorFiltersReset) && (
                <div className="results-vector-filters-trailing">
                  {!isCurrent && !isSummary && (
                    <StrategyFilterCompareControl
                      displayFilter={displayFilter}
                      onDisplayFilterChange={setDisplayFilter}
                      rollEnabled={rollIndicatorsOn && isNasdaqFuturesContractType(contractType)}
                      wrapClassName="results-filter-compare-wrap strategy-calendar-filter-wrap"
                    />
                  )}
                  {showVectorFiltersReset && (
                    <div className="results-select-group">
                      <label className="results-select-label" style={{ opacity: 0 }}>Reset</label>
                      <button
                        className="results-reset-filter-btn"
                        onClick={() => {
                          setContractType('NQ');
                          setContractSize(1);
                          if (!isSummary && !isCurrent) {
                            setDateRangeFilter('all-time');
                            setCustomStartDate('');
                            setCustomEndDate('');
                            setDisplayFilter({
                              ...DEFAULT_DISPLAY_FILTER,
                              days: { ...DEFAULT_DISPLAY_FILTER.days },
                            });
                          }
                        }}
                        aria-label="Reset all filters to defaults"
                      >
                        Reset Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="results-sheet-combo-cta-slot results-sheet-combo-cta-slot--filters">
                {renderCustomComboCta()}
              </div>
            </div>
            </div>
          </div>
          )}
          </div>
          <div className="results-sheet-tabbar" aria-label={`${getResultsNavLabel()} sheets`}>
            <div className="results-sheet-picker" ref={sheetPickerRef}>
              <button
                type="button"
                className={`results-sheet-picker-btn ${sheetPickerOpen ? 'open' : ''}`}
                onClick={() => setSheetPickerOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={sheetPickerOpen}
                aria-label="All sheets"
                title="All sheets"
              >
                <IconList size={16} />
              </button>
              {sheetPickerOpen && (
                <ul
                  className="results-sheet-picker-menu"
                  role="listbox"
                  aria-label="Sheets"
                >
                  {(() => {
                    const splitIdx = sheetOptions.indexOf(MULTI_STRAT_SHEET_NAME);
                    const before = splitIdx >= 0 ? sheetOptions.slice(0, splitIdx) : sheetOptions;
                    const after = splitIdx >= 0 ? sheetOptions.slice(splitIdx) : [];
                    const renderRealItem = (opt) => {
                      const isActive = opt === selectedSheet;
                      return (
                        <li key={opt}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`results-sheet-picker-item ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              handleSheetChange({ target: { value: opt } });
                              setSheetPickerOpen(false);
                            }}
                          >
                            <span className="results-sheet-picker-check" aria-hidden="true">
                              {isActive ? <IconCheck size={14} /> : null}
                            </span>
                            <span className="results-sheet-picker-label">{labelForSheet(opt)}</span>
                          </button>
                        </li>
                      );
                    };
                    return (
                      <>
                        {before.map(renderRealItem)}
                        {customCombos.map((combo) => {
                          const value = `custom:${combo.id}`;
                          const isActive = value === selectedSheet;
                          return (
                            <li key={value}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                className={`results-sheet-picker-item ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                  handleSheetChange({ target: { value } });
                                  setSheetPickerOpen(false);
                                }}
                              >
                                <span className="results-sheet-picker-check" aria-hidden="true">
                                  {isActive ? <IconCheck size={14} /> : null}
                                </span>
                                <span className="results-sheet-picker-label">{combo.name}</span>
                              </button>
                            </li>
                          );
                        })}
                        {after.map(renderRealItem)}
                      </>
                    );
                  })()}
                  {canArrangeTabs ? (
                    <li role="presentation" className="results-sheet-picker-foot">
                      <button
                        type="button"
                        className="results-sheet-picker-item results-sheet-picker-arrange"
                        onClick={() => { setSheetPickerOpen(false); setArrangeOpen(true); }}
                      >
                        <span className="results-sheet-picker-check" aria-hidden="true">⇅</span>
                        <span className="results-sheet-picker-label">Arrange tabs…</span>
                      </button>
                    </li>
                  ) : null}
                </ul>
              )}
            </div>
            <div className="results-sheet-tabs-wrap">
              <nav className="results-sheet-tabs" ref={sheetTabsRef} aria-label="Sheet tabs">
              {/* Render order: Current Results → Past Results → Summary →
                  [custom combos] → Multi-Strat → strategies. The split point
                  is the position of MULTI_STRAT_SHEET_NAME in sheetOptions
                  so user combos sit right under Summary where the user
                  expects "my saved stuff" to live. */}
              {(() => {
                const splitIdx = sheetOptions.indexOf(MULTI_STRAT_SHEET_NAME);
                const tabsBeforeCombos = splitIdx >= 0 ? sheetOptions.slice(0, splitIdx) : sheetOptions;
                const tabsAfterCombos = splitIdx >= 0 ? sheetOptions.slice(splitIdx) : [];
                const renderRealTab = (opt) => {
                  const optIsInternal = internalSheetSet.has(String(opt).trim().toLowerCase());
                  return (
                    <button
                      key={opt}
                      type="button"
                      className={`results-sheet-tab ${selectedSheet === opt ? 'active' : ''}${optIsInternal ? ' is-internal' : ''}`}
                      onClick={() => handleSheetChange({ target: { value: opt } })}
                      title={optIsInternal
                        ? `${labelForSheet(opt)} — Internal (admins only)`
                        : labelForSheet(opt)}
                    >
                      {labelForSheet(opt)}
                    </button>
                  );
                };
                return (
                  <>
                    {tabsBeforeCombos.map(renderRealTab)}
                    {customCombos.map((combo) => {
                      const value = `custom:${combo.id}`;
                      const isActive = selectedSheet === value;
                      return (
                        <span
                          key={value}
                          className={`results-sheet-tab results-sheet-tab-custom ${isActive ? 'active' : ''}`}
                        >
                          <button
                            type="button"
                            className="results-sheet-tab-custom-name"
                            onClick={() => handleSheetChange({ target: { value } })}
                            title={combo.name}
                          >
                            {combo.name}
                          </button>
                          <button
                            type="button"
                            className="results-sheet-tab-custom-edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCustomComboBuilder(combo);
                            }}
                            aria-label={`Edit ${combo.name}`}
                            title="Edit strat combo"
                          >
                            ✎
                          </button>
                        </span>
                      );
                    })}
                    {tabsAfterCombos.map(renderRealTab)}
                  </>
                );
              })()}
              </nav>
              {sheetTabsScrollHintRight && (
                <div
                  className="results-sheet-tabs-scroll-hint"
                  aria-hidden="true"
                  title="Scroll for more strategies"
                >
                  <IconChevronRight size={14} color="rgba(165, 180, 252, 0.9)" />
                </div>
              )}
            </div>
            {canArrangeTabs ? (
              <button
                type="button"
                className="results-arrange-tabs-btn"
                onClick={() => setArrangeOpen(true)}
                title="Brand Admin: tab order, who sees each tab, and contracts"
                aria-label="Arrange tabs"
              >
                <span className="results-arrange-tabs-icon" aria-hidden="true">⇅</span>
                <span className="results-arrange-tabs-label">Arrange tabs</span>
              </button>
            ) : null}
          </div>
          <ArrangeResultsTabs open={arrangeOpen} onClose={closeArrange} />

          <div className="results-dash-header">
        <div>
          {!isPastResults && isCurrent && model.weekLabel && (
            <p className="results-dash-week">Week of {formatWeekLabel(model.weekLabel)}</p>
          )}
          {!isPastResults && !isCurrent && !isSummary && (
            <p className="results-dash-week">
              Historical Performance Data
              {selectedSheetIsInternal ? (
                <span
                  className="results-internal-badge"
                  title="Internal — visible to admins only. Members do not see this strategy anywhere."
                >
                  Internal
                </span>
              ) : null}
            </p>
          )}
          {!isPastResults && isSummary && (
            <p className="results-dash-week">All Strategies Comparison</p>
          )}
          {!isPastResults && (
          <p className="results-dash-subtitle">
            {isCurrent
              ? 'Current weekly results are loaded automatically for fast viewing.'
              : isSummary
                ? 'Compare all strategies side-by-side to easily identify top performers and key metrics.'
                : isMultiStrat
                  ? 'Combined view: Grass Fed Prime Beef (Current version 2.1C) on Mon / Wed / Fri, Tuesday Special (Previous 2.2A) on Tue, & Thursday Special on Thu.'
                  : isCustomCombo && customComboDescription
                    ? customComboDescription
                    : 'View detailed performance metrics and weekly breakdown for this strategy.'
            }
          </p>
          )}
        </div>
        <div className="results-dash-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {!isPastResults && !isCurrent && !isSummary && (
            <div className="results-data-notice-inline" role="status">
              <span className="results-data-notice-inline-text">
                {isVectorNtTest
                  ? 'Data syncs automatically from the add-on in NinjaTrader.'
                  : getDataSourceNote({ withDisclaimer: false })}
              </span>
              <button
                type="button"
                className="results-data-notice-info-btn"
                aria-label="About this data"
                aria-expanded={dataNoticeOpen}
                onClick={() => setDataNoticeOpen((v) => !v)}
                onBlur={() => setDataNoticeOpen(false)}
              >
                <IconInfo size={15} color="rgba(129, 140, 248, 0.95)" />
                {dataNoticeOpen && (
                  <span className="results-data-notice-popover" role="tooltip">
                    {isVectorNtTest
                      ? 'Data syncs from the add-on in NinjaTrader. Third-party strategy data is for analytics only and may be historical, simulated, or model-based, not recommendations or guarantees.'
                      : getDataSourceNote()}
                  </span>
                )}
              </button>
            </div>
          )}
          {isCurrent && rows.length > 0 && !error && (
            <>
              <div className="strategy-view-toggle">
                <button
                  type="button"
                  className={`strategy-view-btn ${showRaw ? 'active' : ''}`}
                  onClick={() => setShowRaw((v) => !v)}
                >
                  Spreadsheet
                </button>
              </div>
              {isVectorNtTest && user?.email && (
                <a
                  className="secondary"
                  href={API_ENDPOINTS.vectorNtExport('Current Results', user.email)}
                  download
                >
                  Download CSV
                </a>
              )}
            </>
          )}
        </div>
      </div>

      {!isPastResults && (isCurrent || isSummary) && (
        <div className="results-data-notice" style={{ marginTop: '1rem' }}>
          <span className="results-data-notice-icon" aria-hidden="true">
            <IconInfo size={18} color="rgba(129, 140, 248, 0.95)" />
          </span>
          <span className="results-data-notice-text">
            {isVectorNtTest
              ? 'Data syncs from the add-on in NinjaTrader. Third-party strategy data is for analytics only and may be historical, simulated, or model-based, not recommendations or guarantees.'
              : getDataSourceNote()}
          </span>
        </div>
      )}
      {loading && showVectorLoadingSkeleton && (
        <VectorResultsLoadingState
          variant="dashboard"
          isCurrent={isCurrent}
          isSummary={isSummary}
          sheetLabel={selectedSheet}
        />
      )}

      {!loading && !error && isCurrent && !showRaw && (
        <>
          <SummaryCards summary={model.summary} contractType={contractType} contractSize={contractSize} />

          <ResultsControls
            search={search}
            onSearch={setSearch}
            showLosersOnly={showLosersOnly}
            onToggleLosers={setShowLosersOnly}
            sortBy={sortBy}
            onSortChange={setSortBy}
            categoryOptions={categoryOptions}
            selectedCategory={category}
            onCategoryChange={setCategory}
            hideLosersToggle={isCurrent}
          />

          {filteredSortedGroups.map((group) => (
            <div key={group.name} className="results-group">
              <div className="results-group-list">
                {group.strategies.map((s) => (
                  <StrategyRowCard
                    key={s.strategyName}
                    strategy={s}
                    category={group.name}
                    contractType={contractType}
                    contractSize={contractSize}
                    onStrategyClick={handleCurrentStrategyClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {!loading && !error && showRaw && isCurrent && (
        <SpreadsheetView
          rawRows={rows}
          sort={currentSpreadsheetSort}
          onSortChange={setCurrentSpreadsheetSort}
        />
      )}

      {isPastResults && (
        <>
          {pastResultsLoading ? (
            <div className="results-spreadsheet-loading" role="status" aria-live="polite">
              <IconRefresh size={20} />
              <span>Loading Past Results…</span>
            </div>
          ) : pastResultsError ? (
            <div className="results-spreadsheet-error" role="alert">{pastResultsError}</div>
          ) : pastResultsRows.length === 0 ? (
            <div className="results-spreadsheet-empty">No data in Past Results.</div>
          ) : (
            <SpreadsheetView
              rawRows={pastResultsRows}
              sort={pastResultsSort}
              onSortChange={setPastResultsSort}
            />
          )}
        </>
      )}

      {!loading && !error && !isCurrent && !isSummary && !isPastResults && (
        <StrategyDetailsView
          sheetName={labelForSheet(displaySheetName)}
          metrics={strategyMetrics}
          metricsBaseline={strategyMetricsBaseline}
          rawRows={filteredStrategySheetRows}
          rawRowsForCalendar={journeyRows}
          journey={{ liveSince, view: journeyView, onChange: setJourneyView }}
          // Stock / ETF and crypto tabs have no contract toggle: show their P&L as recorded.
          contractType={showContractTypeToggle ? contractType : 'NQ'}
          contractSize={showContractTypeToggle ? contractSize : 1}
          displayFilter={displayFilter}
          onDisplayFilterChange={setDisplayFilter}
          vectorDateRangeFilter={dateRangeFilter}
          onVectorDateRangeChange={(value) => {
            if (value === 'custom') setShowCustomDateModal(true);
            else setDateRangeFilter(value);
          }}
          vectorDataSource={isVectorNtTest ? 'ninjaAddon' : 'spreadsheet'}
          hideDataNotice
          isInternal={selectedSheetIsInternal}
          viewerIsPro={isPro}
          onUpgrade={onUpgrade}
        />
      )}

      {!loading && isSummary && user?.email && (
        <StrategySummaryTable 
          user={user}
          contractType={contractType}
          contractSize={contractSize}
          sheetLabels={sheetLabels}
          internalStrategyNames={internalSheetNames}
          isAdminActual={isAdminActual}
          adminViewAs={adminViewAs}
          portfolioAnalyticsUrl={
            isVectorNtTest ? API_ENDPOINTS.vectorNtPortfolioAnalytics(user.email) : undefined
          }
        />
      )}

      {/* Custom Date Range Modal (Vector Results) */}
      {showCustomDateModal && ReactDOM.createPortal(
        <>
          <div className="video-modal-overlay" onClick={() => setShowCustomDateModal(false)} />
          <div className="custom-date-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-date-header">
              <h2>Select Custom Date Range</h2>
              <button className="video-modal-close" onClick={() => setShowCustomDateModal(false)}>
                ×
              </button>
            </div>
            <div className="custom-date-body">
              <div className="custom-date-field">
                <label htmlFor="custom-start-date">Start Date</label>
                <input
                  id="custom-start-date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-field">
                <label htmlFor="custom-end-date">End Date</label>
                <input
                  id="custom-end-date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-actions">
                <button
                  className="custom-date-cancel"
                  onClick={() => {
                    setShowCustomDateModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="custom-date-apply"
                  onClick={() => {
                    if (customStartDate || customEndDate) {
                      setDateRangeFilter('custom');
                      setShowCustomDateModal(false);
                    }
                  }}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Custom Date Range Modal (My Trades) */}
      {mthShowCustomDateModal && ninjatraderView === 'history' && ReactDOM.createPortal(
        <>
          <div className="video-modal-overlay" onClick={() => setMthShowCustomDateModal(false)} />
          <div className="custom-date-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-date-header">
              <h2>Select Custom Date Range</h2>
              <button className="video-modal-close" onClick={() => setMthShowCustomDateModal(false)}>×</button>
            </div>
            <div className="custom-date-body">
              <div className="custom-date-field">
                <label htmlFor="mth-custom-start-date">Start Date</label>
                <input
                  id="mth-custom-start-date"
                  type="date"
                  value={mthCustomStartDate}
                  onChange={(e) => setMthCustomStartDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-field">
                <label htmlFor="mth-custom-end-date">End Date</label>
                <input
                  id="mth-custom-end-date"
                  type="date"
                  value={mthCustomEndDate}
                  onChange={(e) => setMthCustomEndDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-actions">
                <button className="custom-date-cancel" onClick={() => setMthShowCustomDateModal(false)}>Cancel</button>
                <button
                  className="custom-date-apply"
                  onClick={() => {
                    if (mthCustomStartDate || mthCustomEndDate) {
                      setMthDateRangeFilter('custom');
                      setMthShowCustomDateModal(false);
                    }
                  }}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
        </>
      ))}

      {/* My Results Tab Content */}
      {dashboardView === 'ninjatrader' && (
        ninjaAccessBlocking && showNinjaAccessWaitUi ? (
          <div className="upgrade-required-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="nt-spinner" style={{ margin: '0 auto 1rem', width: 40, height: 40 }} />
              <p style={{ color: 'rgba(255,255,255,0.7)' }}>Checking access...</p>
            </div>
          </div>
        ) : ninjaAccessBlocking ? null : !signedIn ? (
          <div className="upgrade-required-page">
            <div className="upgrade-required-content">
              <div className="pro-lock-icon">
                <IconLock size={48} />
              </div>
              <h2 className="upgrade-required-title">My Results</h2>
              <p className="pro-lock-subtitle">Sign in to continue</p>
              <p className="upgrade-required-description" style={{ marginBottom: '1.5rem' }}>
                Sign in to connect accounts and view your live trading results in {appName}.
              </p>
              <button
                type="button"
                className="pro-lock-upgrade-button"
                onClick={() => onNavigate && onNavigate('login')}
              >
                Sign in
              </button>
            </div>
          </div>
        ) : !myResultsAccess ? (
          <div className="upgrade-required-page">
            <div className="upgrade-required-content">
              <div className="pro-lock-icon">
                <IconLock size={48} />
              </div>
              <h2 className="upgrade-required-title">Access Required</h2>
              <p className="upgrade-required-description">
                My Results is available to Admin users only.
              </p>
              <button
                type="button"
                className="back-button"
                onClick={() => onNavigate && onNavigate('results')}
                style={{
                  marginTop: '1rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                Back to {getResultsNavLabel()}
              </button>
            </div>
          </div>
        ) : (
          <>
          <div className="results-dash-header results-dash-header-my-results">
            {/* Row 1: filters left, primary actions right — full width so filters stay horizontal */}
            <div className="results-dash-toolbar-row">
              <div className="results-controls-row results-controls-row-my-results">
                <div className="results-select-group">
                  <label className="results-select-label" style={{ textAlign: 'left' }}>Trader View</label>
                  <select
                    className="results-sheet-select-inline"
                    aria-label="Select My Results view"
                    value={ninjatraderView}
                    onChange={(e) => setNinjatraderView(e.target.value)}
                  >
                    <option value="history">My Trades</option>
                    <option value="current">Current Results</option>
                    <option value="risk">Risk Management</option>
                    <option value="summary">Accounts</option>
                  </select>
                </div>
                {ninjatraderView === 'summary' && (
                  <div className="results-select-group">
                    <label className="results-select-label" style={{ textAlign: 'left' }}>Platform</label>
                    <select
                      className="results-sheet-select-inline"
                      aria-label="Filter accounts by platform"
                      value={myResultsAccountPlatform}
                      onChange={(e) => setMyResultsAccountPlatform(e.target.value)}
                    >
                      <option value="all">All platforms</option>
                      <option value="ninjatrader">NinjaTrader</option>
                      <option value="tradovate">Tradovate</option>
                    </select>
                  </div>
                )}
                {ninjatraderView === 'history' && (
                  <>
                    <div className="results-select-group">
                      <label className="results-select-label" style={{ textAlign: 'left' }}>Accounts</label>
                      <div className="mth-accounts-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <select
                          className="results-sheet-select-inline"
                          aria-label="Filter by account"
                          value={mthSelectedAccounts === 'all' ? '__all__' : (mthSelectedAccounts?.[0] ?? '__all__')}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMthSelectedAccounts(v === '__all__' ? 'all' : [v]);
                          }}
                          disabled={mthAccountsLoading}
                        >
                          <option value="__all__">All {mthIncludeInactiveAccounts ? 'Accounts' : 'Active'}</option>
                          {(mthIncludeInactiveAccounts ? mthAccounts : mthAccounts.filter(a => a.is_active !== false)).map(a => (
                            <option key={a.account_name} value={a.account_name}>
                              {a.account_name}{!a.is_active ? ' (inactive)' : ''}
                            </option>
                          ))}
                        </select>
                        <label className="mth-include-inactive-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={mthIncludeInactiveAccounts}
                            onChange={(e) => setMthIncludeInactiveAccounts(e.target.checked)}
                            aria-label="Include inactive accounts"
                          />
                          Include inactive
                        </label>
                      </div>
                    </div>
                    <div className="results-select-group">
                      <label className="results-select-label" style={{ textAlign: 'left' }}>Date Range</label>
                      <select
                        className="results-sheet-select-inline"
                        aria-label="Select date range"
                        value={mthDateRangeFilter}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'custom') {
                            setMthShowCustomDateModal(true);
                          } else {
                            setMthDateRangeFilter(value);
                          }
                        }}
                      >
                        <option value="all-time">All Time</option>
                        <option value="last-30-days">Last 30 Days</option>
                        <option value="last-3-months">Last 3 Months</option>
                        <option value="last-6-months">Last 6 Months</option>
                        <option value="last-12-months">Last 12 Months</option>
                        <option value="2024">2024</option>
                        <option value="2025">2025</option>
                        <option value="2026">2026</option>
                        <option value="custom">Custom Range...</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div className={`results-dash-actions ${ninjatraderView === 'summary' ? 'results-dash-actions-accounts' : ''}`} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'nowrap' }}>
                {(ninjatraderView === 'current' || ninjatraderView === 'history' || ninjatraderView === 'risk') && (
                  <button
                    className="nt-btn nt-btn-secondary"
                    onClick={() => setNinjatraderView('summary')}
                  >
                    Manage Accounts
                  </button>
                )}
                {ninjatraderView === 'summary' && (
                  <>
                    <button
                      className="nt-btn nt-btn-secondary"
                      onClick={async () => {
                        if (ntAccountsRefreshing || !ninjaTraderDashboardRef.current?.refreshData) return;
                        setNtAccountsRefreshing(true);
                        try {
                          await ninjaTraderDashboardRef.current.refreshData();
                        } finally {
                          setNtAccountsRefreshing(false);
                        }
                      }}
                      disabled={ntAccountsRefreshing}
                      title="Refresh account data"
                    >
                      <IconRefresh size={16} style={ntAccountsRefreshing ? { animation: 'spin 0.8s linear infinite' } : undefined} /> {ntAccountsRefreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                    <button
                      className="nt-btn nt-btn-secondary"
                      onClick={() => {
                        if (ninjaTraderDashboardRef.current && ninjaTraderDashboardRef.current.openQuickSetup) {
                          ninjaTraderDashboardRef.current.openQuickSetup();
                        }
                      }}
                    >
                      <IconZap size={16} /> Quick Setup
                    </button>
                    <button
                      className="nt-btn nt-btn-secondary"
                      onClick={() => {
                        if (ninjaTraderDashboardRef.current && ninjaTraderDashboardRef.current.openConnectModal) {
                          ninjaTraderDashboardRef.current.openConnectModal();
                        }
                      }}
                    >
                      + Accounts
                    </button>
                    <div className="nt-view-toggle nt-view-toggle-header">
                      <button
                        className={`nt-view-btn ${ntCardViewMode === 'table' ? 'active' : ''}`}
                        onClick={() => setNtCardViewMode('table')}
                        title="Table View"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <line x1="1" y1="3" x2="15" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <line x1="1" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <line x1="1" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                      <button
                        className={`nt-view-btn ${ntCardViewMode === 'cards' ? 'active' : ''}`}
                        onClick={() => setNtCardViewMode('cards')}
                        title="Card View"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Row 2: page title + subtitle (not inline with filters) */}
            {ninjatraderView === 'current' && (
              <div className="results-dash-title-block">
                <p className="results-dash-week">
                  This Week's Live Trading Results
                </p>
                <p className="results-dash-subtitle">
                  View daily P&L breakdown for each of your connected NinjaTrader accounts this week.
                </p>
              </div>
            )}
            {ninjatraderView === 'risk' && (
              <div className="results-dash-title-block">
                <p className="results-dash-week">
                  Real-Time Risk Monitoring
                </p>
                <p className="results-dash-subtitle">
                  Monitor blowout risk and % rule compliance across all your NinjaTrader accounts using {getResultsNavLabel()} data.
                </p>
              </div>
            )}
            {ninjatraderView === 'history' && (
              <div className="results-dash-title-block">
                <p className="results-dash-week">
                  My Trades
                </p>
                <p className="results-dash-subtitle">
                  Calendar view of all historical trades across your connected NinjaTrader accounts.
                  {mthDateRangeFilter === 'all-time' && (
                    <span className="mth-all-time-hint"> Use the calendar arrows to navigate to older months.</span>
                  )}
                </p>
              </div>
            )}
          </div>

          {ninjatraderView === 'current' ? (
            <NinjaTraderDashboard ref={ninjaTraderDashboardRef} user={user} viewMode="current" sheetNames={sheetNames} isAdmin={isAdmin} accountPlatformFilter={myResultsAccountPlatform} />
          ) : ninjatraderView === 'history' ? (
            <NinjaTraderDashboard
              ref={ninjaTraderDashboardRef}
              user={user}
              viewMode="history"
              sheetNames={sheetNames}
              isAdmin={isAdmin}
              mthDateRangeFilter={mthDateRangeFilter}
              mthCustomStartDate={mthCustomStartDate}
              mthCustomEndDate={mthCustomEndDate}
              mthSelectedAccounts={mthSelectedAccounts}
              accountPlatformFilter={myResultsAccountPlatform}
            />
          ) : ninjatraderView === 'risk' ? (
            <NinjaTraderDashboard ref={ninjaTraderDashboardRef} user={user} viewMode="risk" sheetNames={sheetNames} isAdmin={isAdmin} accountPlatformFilter={myResultsAccountPlatform} />
          ) : (
            <NinjaTraderDashboard 
              ref={ninjaTraderDashboardRef} 
              user={user} 
              viewMode="summary" 
              isAdmin={isAdmin}
              cardViewMode={ntCardViewMode}
              sheetNames={sheetNames}
              connectButtonInParent={true}
              accountPlatformFilter={myResultsAccountPlatform}
            />
          )}
          </>
        )
      )}
      <CustomMultistratBuilderModal
        isOpen={builderState.open}
        existing={builderState.editing}
        strategies={sheetNames}
        saving={builderSaving}
        error={builderError}
        onClose={closeCustomComboBuilder}
        onSave={handleSaveCustomCombo}
        onDelete={builderState.editing ? handleDeleteCustomCombo : undefined}
      />
    </div>
  );
}

export default ResultsDashboard;

